import { SELF_EMPLOYED, SELF_EMPLOYED_1099 } from "../../constants/wage_forms";
import { INDIVIDUAL_NAME_PROPS, INCOME_TYPE_ENUM, MARITAL_STATUS_ENUM, ADDRESS_PROPS, ADDRESS_STATE_PROPS, CREDIT_SCORE_VALUES } from "../constants";
import { MARRIED } from "../../constants/marital_status";
import { year_to_payment } from "../../calculations/income";
import { SEASONAL_INCOME, OTHER_INCOME } from "../../constants/additional_income_types";
import { OWN, OWN_IN_TRUST, RENT } from "../../constants/renting_or_owning";
import { YES } from "../../constants/yes_no";

export function populateName(builder, { borrower }) {
  for (const [key, name] of Object.entries(INDIVIDUAL_NAME_PROPS)) {
    if (borrower[key]) {
      builder.ele(name).txt(borrower[key]);
    }
  }
}

export function populateContactPoints(builder, { borrower }) {
  // typeof check skips {} left by an older import of an empty phone tag.
  const isPhone = value => typeof value === "string" && value !== "";
  const populatedPhoneNumbers = [
    isPhone(borrower.home_phone) ? { value: borrower.home_phone, type: "Home" } : null,
    isPhone(borrower.cell_phone) ? { value: borrower.cell_phone, type: "Mobile" } : null,
    isPhone(borrower.work_phone) ? { value: borrower.work_phone, type: "Work" } : null
  ].filter(Boolean);

  for (const { value, type } of populatedPhoneNumbers) {
    builder
      .ele("CONTACT_POINT")
        .ele("CONTACT_POINT_TELEPHONE")
          .ele("ContactPointTelephoneValue").txt(value).up()
        .up()
      .ele("CONTACT_POINT_DETAIL")
        .ele("ContactPointRoleType").txt(type);
  }

  if (borrower.email) {
    builder
      .ele("CONTACT_POINT")
        .ele("CONTACT_POINT_EMAIL")
          .ele("ContactPointEmailValue").txt(borrower.email);
  }
}

export function populateIdentificationVerification(builder, { document, borrower }) {
  if (borrower.marital_status === MARRIED
      && document.applicant.borrowers[borrower.married_to]) {
    populateName(builder.ele("SPOUSE_INFORMATION").ele("NAME"), {
      borrower: document.applicant.borrowers[borrower.married_to]
    });
  }
}

export function populateAddress(builder, { borrower }) {
  for (const [key, name] of Object.entries(ADDRESS_PROPS)) {
    if (borrower[key]) {
      builder.ele(name).txt(borrower[key]);
    }
  }

  if (borrower.state) {
    builder.ele(
      borrower.state.length === 2
        ? ADDRESS_STATE_PROPS.state_code
        : ADDRESS_STATE_PROPS.state_name
    ).txt(borrower.state);
  }
}

export function populateResidenceAddress(builder, { borrower }) {
  if (borrower.current_address) builder.ele("AddressLineText").txt(borrower.current_address);
  if (borrower.current_address_2) builder.ele("AddressAdditionalLineText").txt(borrower.current_address_2);
  if (borrower.city) builder.ele("CityName").txt(borrower.city);
  builder.ele("CountryCode").txt("US");
  if (borrower.zip_code) builder.ele("PostalCode").txt(borrower.zip_code);
  if (borrower.state) {
    builder.ele(
      borrower.state.length === 2
        ? ADDRESS_STATE_PROPS.state_code
        : ADDRESS_STATE_PROPS.state_name
    ).txt(borrower.state);
  }
}

export function populateIncomeItemDetail(builder, { incomeSource, incomeOther, incomeType, otherDescription, componentAnnual, reoNetRental }) {
  // Invest REO Schedule E net rental income/loss, scoped to the owning borrower.
  // Exported as 1e "Other" income (labeled "Net Rental Income") so POINT keeps it
  // on this borrower only. Not employment income; may be positive or negative.
  if (reoNetRental) {
    const monthly = year_to_payment(componentAnnual, 12);
    builder.ele("CurrentIncomeMonthlyTotalAmount").txt(monthly.toDecimalPlaces(2));
    builder.ele("EmploymentIncomeIndicator").txt(false);
    builder.ele("IncomeType").txt("Other");
    builder.ele("IncomeTypeOtherDescription").txt("Net Rental Income");
    return;
  }
  if (!incomeSource && !incomeOther) {
    throw new Error("populateIncomeItemDetail called with no incomeSource or incomeOther");
  }

  // Employment income is exported one item per component (Base / Overtime /
  // Bonus / Commissions / Tips); componentAnnual + incomeType come from
  // income_export_components via the CURRENT_INCOME_ITEM @forEach.
  const yearlyIncome = incomeSource
    ? componentAnnual
    : incomeOther.yearly_amount;
  const monthlyIncome = year_to_payment(yearlyIncome, 12);

  const isDscr = incomeSource && incomeSource.calculator === "NonQM DSCR";
  builder.ele("CurrentIncomeMonthlyTotalAmount").txt(monthlyIncome.toDecimalPlaces(2));
  // DSCR rental income is NOT employment income (it's Section 1e other income).
  builder.ele("EmploymentIncomeIndicator").txt(!!incomeSource && !isDscr);

  if (incomeSource) {
    builder.ele("IncomeType").txt(incomeType);
    // DSCR (and any other "Other" income component) carries a description so the
    // LOS shows what the Other-income line is (e.g. "DSCR Rental Income").
    if (incomeType === "Other" && otherDescription) {
      builder.ele("IncomeTypeOtherDescription").txt(otherDescription);
    }
  } else {
    let other;

    if (INCOME_TYPE_ENUM[incomeOther.income_type]) {
      builder.ele("IncomeType").txt(INCOME_TYPE_ENUM[incomeOther.income_type]);
      other = (incomeOther.income_type === OTHER_INCOME);
    } else {
      builder.ele("IncomeType").txt(INCOME_TYPE_ENUM[OTHER_INCOME]);
      other = true;
    }

    if (other && incomeOther.description) {
      builder.ele("IncomeTypeOtherDescription").txt(incomeOther.description);
    }

    builder.ele("SeasonalIncomeIndicator").txt(incomeOther.income_type === SEASONAL_INCOME);
  }
}

export function populateResidenceDetail(builder, { borrower }) {
  const currentlyOwning = [OWN, OWN_IN_TRUST].includes(borrower.currently_renting_or_owning);
  const currentlyRenting = borrower.currently_renting_or_owning === RENT;

  builder
    .ele("BorrowerResidencyBasisType")
      .txt(currentlyOwning ? "Own" : currentlyRenting ? "Rent" : "Unknown");

  const yearsAtResidence = parseFloat(borrower.years_at_current_residence) || 0;
  if (yearsAtResidence > 0) {
    builder
      .ele("BorrowerResidencyDurationMonthsCount")
        .txt(Math.round(yearsAtResidence * 12));
  } else if (currentlyRenting) {
    const months = parseFloat(borrower.how_long_in_current_residence) || 0;
    if (months > 0) {
      builder
        .ele("BorrowerResidencyDurationMonthsCount")
          .txt(months);
    }
  }

  builder.ele("BorrowerResidencyType").txt("Current");
}

// MISMO 3.4 dates must be ISO (YYYY-MM-DD). The app stores date_of_birth
// inconsistently — some borrowers as ISO, others as MM/DD/YYYY — so a raw
// passthrough sent e.g. "04/30/1965", which POINT couldn't parse (it read
// undefined/undefined and fell back to 01/01/current-year). Normalize here.
export function toIsoDate(value) {
  if (!value) return value;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; // already ISO
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s); // M/D/YYYY or MM/DD/YYYY
  if (m) {
    return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  }
  return s; // unknown format — leave as-is rather than corrupt it
}

export function populateBorrowerDetail(builder, { borrower }) {
  if (borrower.date_of_birth) {
    builder.ele("BorrowerBirthDate").txt(toIsoDate(borrower.date_of_birth));
  }

  builder.ele("CommunityPropertyStateResidentIndicator").txt("false");
  builder.ele("DependentCount").txt("0");
  builder.ele("DomesticRelationshipIndicator").txt("false");

  builder
    .ele("MaritalStatusType")
      .txt(MARITAL_STATUS_ENUM[borrower.marital_status] || "NotProvided");

  builder
    .ele("SelfDeclaredMilitaryServiceIndicator")
      .txt(borrower.is_veteran === YES);

  builder.ele("SpousalVABenefitsEligibilityIndicator").txt("false");
}

// One CREDIT_SCORE export item per bureau score we actually have (from a credit
// pull / import). When no individual bureau scores exist (older files, or a
// manually-entered range), fall back to a single score built from the range
// bucket so the export keeps its prior behavior. Each item is shaped for the
// mismo_config @forEach: it merges { scoreItem } into the node context.
const CREDIT_SCORE_BUREAUS = [
  ["credit_score_equifax", "Equifax"],
  ["credit_score_experian", "Experian"],
  ["credit_score_transunion", "TransUnion"]
];

export function creditScoreExportItems(borrower) {
  const items = [];

  for (const [field, bureau] of CREDIT_SCORE_BUREAUS) {
    const value = parseInt(borrower[field], 10);
    if (Number.isFinite(value)) {
      items.push({ scoreItem: { value: String(value), bureau } });
    }
  }

  if (items.length) {
    return items;
  }

  const rangeValue = CREDIT_SCORE_VALUES[borrower.lowest_credit_mid_score];
  if (rangeValue) {
    items.push({ scoreItem: { value: String(rangeValue), bureau: null } });
  }

  return items;
}

export function populateGovernmentMonitoring(builder) {
  const detail = builder.ele("GOVERNMENT_MONITORING_DETAIL");
  detail.ele("HMDAEthnicityRefusalIndicator").txt("false");
  detail.ele("HMDAGenderRefusalIndicator").txt("false");
  detail.ele("HMDARaceRefusalIndicator").txt("false");
}

export function populateEmployerAddress(builder, { incomeSource }) {
  const borrower = {
    current_address: incomeSource.employment_information.address,
    city: incomeSource.employment_information.city,
    state: incomeSource.employment_information.state,
    zip_code: incomeSource.employment_information.zip
  };

  populateAddress(builder, { borrower });
}

export function populateEmployerEmployment(builder, { incomeSource, employerIndex }) {
  const selfEmployed = [
    SELF_EMPLOYED,
    SELF_EMPLOYED_1099
  ].includes(incomeSource.w2_or_self_employed);

  builder.ele("EmploymentBorrowerSelfEmployedIndicator").txt(selfEmployed);

  builder.ele("EmploymentClassificationType").txt(employerIndex === 0 ? "Primary" : "Secondary");

  if (incomeSource.employment_information.position) {
    builder
      .ele("EmploymentPositionDescription")
        .txt(incomeSource.employment_information.position);
  }

  if (incomeSource.employment_information.start_date) {
    builder
      .ele("EmploymentStartDate")
        .txt(incomeSource.employment_information.start_date);
  }

  builder.ele("EmploymentStatusType").txt("Current");

  const yearsOnJob = parseFloat(incomeSource.years_on_the_current_job) || 0;
  const monthsOnJob = parseFloat(incomeSource.months_on_the_current_job) || 0;
  const totalJobMonths = Math.round(yearsOnJob * 12 + monthsOnJob);
  if (totalJobMonths > 0) {
    builder
      .ele("EmploymentTimeMonthsCount")
        .txt(String(totalJobMonths));
  }

  const lineOfWorkYears = parseFloat(incomeSource.employment_information.line_of_work_years) || 0;
  const lineOfWorkMonths = parseFloat(incomeSource.employment_information.line_of_work_months) || 0;
  const totalLineOfWorkMonths = Math.round(lineOfWorkYears * 12 + lineOfWorkMonths);
  if (totalLineOfWorkMonths > 0) {
    builder
      .ele("EmploymentTimeInLineOfWorkMonthsCount")
        .txt(String(totalLineOfWorkMonths));
  }

  builder
    .ele("SpecialBorrowerEmployerRelationshipIndicator")
      .txt(incomeSource.employment_information.employed_by_party_to_transaction === YES);

  const duExt = builder.ele("EXTENSION").ele("OTHER")
    .ele("DU:EMPLOYMENT_EXTENSION");
  duExt.ele("DU:ForeignIncomeIndicator").txt("false");
  duExt.ele("DU:SeasonalIncomeIndicator").txt("false");
}
